import { Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

const RELOAD_KEY = 'stale-build-reload';
/** A second chunk failure for the same URL within this window is a real outage, not a stale build. */
const RELOAD_WINDOW_MS = 30_000;

/**
 * Recovers navigations that fail because the tab is running an old build.
 *
 * Lazy sections ship as hashed chunk files. After a deploy, a tab opened before it
 * still requests the old file names; the server's catch-all answers them with
 * index.html, the module fails to load, and the router drops the navigation, so a
 * sidebar click silently does nothing until the page is refreshed. Instead, load
 * the requested URL with a full page load, which picks up the new build. Open tabs
 * are persisted in localStorage, so they survive the reload.
 */
@Injectable({ providedIn: 'root' })
export class StaleBuildReloadService {
  constructor(private router: Router) {}

  start(): void {
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => {
        if (isChunkLoadError(event.error)) {
          this.reloadInto(event.url);
        }
      });
  }

  private reloadInto(url: string): void {
    const last = readLastReload();
    if (last && last.url === url && Date.now() - last.at < RELOAD_WINDOW_MS) {
      console.error('[StaleBuildReload] Chunk still failing after a reload, not retrying:', url);
      return;
    }
    try {
      sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ url, at: Date.now() }));
    } catch {
      // Without sessionStorage there is no loop guard; a single reload is still the right call.
    }
    this.fullPageLoad(url);
  }

  /** Separate so tests can intercept it; jsdom cannot navigate. */
  protected fullPageLoad(url: string): void {
    window.location.assign(url);
  }
}

/** True for the errors a missing or HTML-substituted lazy chunk produces. */
export function isChunkLoadError(error: unknown): boolean {
  const err = error as { name?: string; message?: string } | null;
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  return /Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
    err.message || '',
  );
}

function readLastReload(): { url: string; at: number } | null {
  try {
    return JSON.parse(sessionStorage.getItem(RELOAD_KEY) || 'null');
  } catch {
    return null;
  }
}
